import type {
    ExtensionContextMenuItem,
    MenuContext,
} from "@magic-md/extension-sdk";
import type { ReactNode } from "react";
import type { ContextMenuItem } from "../components/ContextMenu";
import { contributionRegistry } from "./contribution-registry";
import { renderExtensionActivityIcon } from "./extension-activity-icon";

function explicitIcon(icon: ReactNode): ReactNode | undefined {
    return icon && typeof icon !== "string" ? icon : undefined;
}

function toContextMenuItem(
    item: ExtensionContextMenuItem,
    indexPath: string,
    extensionId: string,
    extensionIcon: ReactNode,
    level = 0,
): ContextMenuItem {
    const icon = explicitIcon(item.icon);
    const fallbackIcon =
        level === 0
            ? renderExtensionActivityIcon(extensionId, extensionIcon, 14)
            : undefined;
    return {
        key: `extension:${indexPath}:${item.key}`,
        label: item.label,
        icon: icon ?? fallbackIcon,
        disabled: item.disabled,
        separator: item.separator,
        children: item.children?.map((child, index) =>
            toContextMenuItem(
                child,
                `${indexPath}.${index}`,
                extensionId,
                extensionIcon,
                level + 1,
            ),
        ),
        onClick: item.onClick
            ? () => {
                void item.onClick?.();
            }
            : undefined,
    };
}

export function buildExtensionContextMenuItems(
    context: MenuContext,
    options: { firstSeparator?: boolean } = {},
): ContextMenuItem[] {
    const sidebarPanels = contributionRegistry.sidebarPanels();
    const items = contributionRegistry
        .buildOwnedMenu(context)
        .map(({ extensionId, item }, index) => {
            const sidebarPanel = sidebarPanels.find(
                (entry) => entry.extensionId === extensionId,
            );
            return toContextMenuItem(
                item,
                `${context.surface}:${extensionId}:${index}`,
                extensionId,
                sidebarPanel?.panel.icon,
            );
        });
    if (items.length > 0 && options.firstSeparator) {
        items[0] = { ...items[0], separator: true };
    }
    return items;
}
